import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { apiClient } from '../utils/api';
import { DateTimeInput } from '../components/DateTimeInput';
import { localizeAppointmentApiMessage } from '../utils/localizeApiMessage';

interface SelectOption {
  id: string;
  name: string;
  price?: number;
  planningRecommendation?: string | null;
  address?: { city?: string };
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface FormData {
  clientId: string;
  shopId: string;
  serviceId: string;
  scheduledAt: string;
  estimatedEndTime: string;
  status: string;
  type: string;
  notes: string;
  clientNotes: string;
}

export function PractitionerAppointmentCreatePage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [shops, setShops] = useState<SelectOption[]>([]);
  const [services, setServices] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  const [form, setForm] = useState<FormData>({
    clientId: '',
    shopId: '',
    serviceId: '',
    scheduledAt: '',
    estimatedEndTime: '',
    status: 'SCHEDULED',
    type: 'CONSULTATION',
    notes: '',
    clientNotes: '',
  });

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const [clientsRes, shopsRes] = await Promise.all([
        apiClient('/api/practitioner/clients?limit=200'),
        apiClient('/api/practitioner/shops'),
      ]);
      const [clientsData, shopsData] = await Promise.all([clientsRes.json(), shopsRes.json()]);

      if (clientsData.success) {
        setClients(
          (clientsData.data.clients || []).map((c: any) => ({
            id: c.id,
            name: `${c.firstName} ${c.lastName} (${c.email || c.phone || ''})`.trim(),
          }))
        );
      }
      if (shopsData.success) {
        setShops(
          (shopsData.data.shops || []).map((s: any) => ({
            id: s.id,
            name: `${s.name}${s.address?.city ? ` - ${s.address.city}` : ''}`,
          }))
        );
      }
      await fetchServicesForShop(form.shopId || undefined);
    } catch (e: any) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesForShop = async (shopId?: string) => {
    try {
      const endpoint = shopId
        ? `/api/practitioner/services?shopId=${encodeURIComponent(shopId)}`
        : '/api/practitioner/services';
      const servicesRes = await apiClient(endpoint);
      const servicesData = await servicesRes.json();
      if (!servicesData.success) return;

      const nextServices = (servicesData.data.services || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        price: s.basePrice,
        planningRecommendation: s.planningRecommendation || null,
      }));
      setServices(nextServices);
      if (form.serviceId && !nextServices.some((s: SelectOption) => s.id === form.serviceId)) {
        setForm((prev) => ({ ...prev, serviceId: '' }));
      }
    } catch (e: any) {
      console.error('Fetch practitioner services error:', e);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchServicesForShop(form.shopId || undefined);
  }, [form.shopId]);

  const handleChange = (key: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);

      const { clientNotes, ...payloadBase } = form;
      const payload = {
        ...payloadBase,
        preparation: {},
        pricing: {},
        reminders: {},
      };

      const resp = await apiClient('/api/practitioner/appointments', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!data?.success) {
        setError(
          localizeAppointmentApiMessage(data?.message) || 'Échec de la création du rendez-vous'
        );
        if (Array.isArray(data?.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
        return;
      }

      navigate('/practitioner/appointments');
    } catch (e: any) {
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/practitioner/appointments')}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour</span>
        </button>

        <button
          type="submit"
          form="practitioner-appointment-form"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde…' : 'Créer'}</span>
        </button>
      </div>

      <h1 className="text-2xl font-display font-bold text-textPrimary mb-2">Nouveau rendez-vous</h1>
      <p className="text-textSecondary mb-6">Création d’un rendez-vous pour vous-même.</p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form id="practitioner-appointment-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Client *</label>
              <select
                className="input-field"
                required
                value={form.clientId}
                onChange={(e) => handleChange('clientId', e.target.value)}
              >
                <option value="">Sélectionner</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Shop *</label>
              <select
                className="input-field"
                required
                value={form.shopId}
                onChange={(e) => handleChange('shopId', e.target.value)}
              >
                <option value="">Sélectionner</option>
                {shops.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Service *</label>
              <select
                className="input-field"
                required
                value={form.serviceId}
                onChange={(e) => handleChange('serviceId', e.target.value)}
              >
                <option value="">Sélectionner</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {services.find((s) => s.id === form.serviceId)?.planningRecommendation && (
                <div
                  className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                  dangerouslySetInnerHTML={{
                    __html:
                      services.find((s) => s.id === form.serviceId)?.planningRecommendation || '',
                  }}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Type</label>
              <select
                className="input-field"
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="CONSULTATION">Consultation</option>
                <option value="TATTOO_SESSION">Séance Tatouage</option>
                <option value="PIERCING">Piercing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Début *</label>
              <DateTimeInput
                required
                className="input-field"
                value={form.scheduledAt}
                onChange={(value) => {
                  handleChange('scheduledAt', value);

                  if (value && !form.estimatedEndTime) {
                    const start = new Date(value);
                    const end = new Date(start.getTime() + 60 * 60 * 1000);

                    handleChange('estimatedEndTime', end.toISOString().slice(0, 16));
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Fin estimée *
              </label>

              <DateTimeInput
                required
                className="input-field"
                value={form.estimatedEndTime}
                onChange={(value) => handleChange('estimatedEndTime', value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Statut</label>
              <select
                className="input-field"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="SCHEDULED">Programmé</option>
                <option value="CONFIRMED">Confirmé</option>
                <option value="IN_PROGRESS">Pris en charge</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Notes</label>
              <textarea
                className="input-field"
                rows={4}
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Notes client
              </label>
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                Les notes du client sont saisies par le client et ne sont pas modifiables ici.
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PractitionerAppointmentCreatePage;
